import { IBooking, IBookingFromPouch } from 'shared-types/index';
import {
    setSearchResults,
    appendSearchResults,
    setLoading,
    setMoreLoading,
    setLoadError,
    setBookmark,
    setSearchQuery,
    setSearchLevel
} from 'app/reducers/globalSearchModal/slice';
import store from 'appRoot/Store';
import { ApiSearchService } from 'app/services/apiSearch/apiSearch.service';
import SearchService from 'app/services/search/search.service';
import { SEARCH_DATE_OPTIONS } from 'app/app.types';
import { FilterType } from 'app/bookings/GlobalBookingSearchModal/GlobalBookingSearchModal';
// import BookingNormaliseService from 'external-services/booking-normalise-service/bookingNormalise.service';

class GlobalSearchModalService {
    /**
     * Search for bookings based on query and filter
     */
    async searchBookings(query: string, filter: FilterType = FilterType.Future, date?: Date): Promise<IBooking[]> {
        try {
            // Validate search query
            if (!this.isValidSearchQuery(query)) {
                store.dispatch(setSearchResults([]));
                return [];
            }

            // Update Redux state
            store.dispatch(setSearchQuery(query));
            store.dispatch(setLoading(true));
            store.dispatch(setLoadError(false));

            // Map filter to search level
            const searchLevel = this.mapFilterToSearchLevel(filter);
            store.dispatch(setSearchLevel(searchLevel));

            let results: IBooking[] = [];

            if (searchLevel === SEARCH_DATE_OPTIONS.ALL) {
                // Use ApiSearchService for ALL search
                const searchResult = await ApiSearchService.searchAllBookings(query, 100, undefined);
                // @ts-ignore
                results = searchResult.bookings.map(booking => {
                    // it should normalize, but consider cost, might just do what we need, if we need more in future, use follow
                    // return BookingNormaliseService.normalizeModelToView(booking as unknown as IBookingFromPouch)
                    booking.bookingEnd = new Date(booking.time);
                    booking.bookingEnd.setMinutes(booking.bookingEnd.getMinutes() + Number(booking.duration));
                    return booking;
                });
                store.dispatch(setBookmark(searchResult.bookmark));
            } else {
                // Use SearchService for Future/Current day search
                results = await SearchService.searchFutureOrToday(query, searchLevel, date || new Date());
                store.dispatch(setBookmark(undefined));
            }

            // Update Redux store with search results
            store.dispatch(setSearchResults(results));
            store.dispatch(setLoading(false));

            return results;
        } catch (error) {
            console.error('Error searching bookings:', error);
            store.dispatch(setLoadError(true));
            store.dispatch(setLoading(false));
            store.dispatch(setSearchResults([]));
            return [];
        }
    }

    async loadMoreResults(query: string): Promise<IBooking[]> {
        try {
            const state = store.getState();
            const { bookmark } = state.globalSearchModalReducer;

            if (!bookmark) {
                return [];
            }

            store.dispatch(setMoreLoading(true));
            store.dispatch(setLoadError(false));

            const searchResult = await ApiSearchService.searchAllBookings(query, 100, bookmark);

            // Append results to existing ones
            store.dispatch(appendSearchResults(searchResult.bookings));
            store.dispatch(setBookmark(searchResult.bookmark));
            store.dispatch(setMoreLoading(false));

            // @ts-ignore
            return searchResult.bookings;
        } catch (error) {
            console.error('Error loading more results:', error);
            store.dispatch(setLoadError(true));
            store.dispatch(setMoreLoading(false));
            return [];
        }
    }

    private isValidSearchQuery(query: string): boolean {
        if (!query || query.trim() === '') {
            return false;
        }

        const trimmedQuery = query.trim();

        // Check if it's a number (minimum 2 digits)
        if (!isNaN(Number(trimmedQuery))) {
            return trimmedQuery.length >= 2;
        }

        // Check if it's text (minimum 2 characters)
        return trimmedQuery.length >= 2;
    }

    private mapFilterToSearchLevel(filter: FilterType): SEARCH_DATE_OPTIONS {
        switch (filter) {
            case FilterType.Current:
                return SEARCH_DATE_OPTIONS.DAY;
            case FilterType.Future:
                return SEARCH_DATE_OPTIONS.FUTURE;
            case FilterType.All:
                return SEARCH_DATE_OPTIONS.ALL;
            default:
                return SEARCH_DATE_OPTIONS.FUTURE;
        }
    }

    /**
     * Clear search results
     */
    clearSearchResults(): void {
        store.dispatch(setSearchResults([]));
    }
}

export default new GlobalSearchModalService();
